import type { MerchantSummary, SiteAudit } from "./types";
import { seeded } from "./simulation";

const POLICY_SCORE: Record<string, number> = {
  Excellent: 94,
  Good: 78,
  Average: 54,
  Poor: 30,
  None: 12,
};

const CANCEL_SCORE: Record<string, number> = {
  "Self-serve": 94,
  Email: 62,
  Phone: 34,
  None: 10,
};

function clamp(n: number, lo = 0, hi = 100) {
  return Math.max(lo, Math.min(hi, Math.round(n)));
}

export function buildSiteAudits(merchants: MerchantSummary[]): SiteAudit[] {
  const audits: SiteAudit[] = merchants.map((m, i) => {
    const rng = seeded(i * 8191 + 61);
    const base = POLICY_SCORE[m.refundPolicy] ?? 50;
    const refundPolicy = clamp(base + (rng() - 0.5) * 10);
    const cancellationEase = clamp((CANCEL_SCORE[m.cancelEase] ?? 50) + (rng() - 0.5) * 8);
    // tcScore is already a trust signal (0..100) → use it as the spine for TC clarity
    const tcClarity = clamp(m.tcScore + (rng() - 0.5) * 10);
    // high cbMult is correlated with murky product pages
    const productClarity = clamp(85 - (m.cbMult - 0.5) * 28 + (rng() - 0.5) * 10);
    // MCC alignment — mostly good, but bad-actor merchants (low refund + high cb) are misaligned
    const isMisaligned = m.refundRatePct < 0.5 && m.cbMult > 1.3;
    const mccAlignment = clamp(
      (isMisaligned ? 40 : 88) + (rng() - 0.5) * 16 + (m.tcScore - 50) * 0.3
    );

    const overall = clamp(
      refundPolicy * 0.2 +
        cancellationEase * 0.22 +
        tcClarity * 0.2 +
        productClarity * 0.2 +
        mccAlignment * 0.18
    );

    const flags: string[] = [];
    if (tcClarity < 40) flags.push("t&c pages missing key clauses (renewal, arbitration)");
    if (refundPolicy < 40) flags.push("refund policy not discoverable from the checkout");
    if (cancellationEase < 40) flags.push(`cancellation requires ${m.cancelEase.toLowerCase()}`);
    if (productClarity < 55)
      flags.push("product pages underspecified — what ships doesn't match copy");
    if (mccAlignment < 55)
      flags.push(
        `site content misaligned with declared mcc ${m.mcc} (${m.mccDesc.toLowerCase()})`
      );
    if (rng() < 0.12) flags.push("privacy policy links broken / point to placeholder");
    if (rng() < 0.08 && m.cbMult > 1.2)
      flags.push("pricing shown as monthly but billed annually");

    return {
      merchantId: m.id,
      overall,
      subscores: {
        tcClarity,
        refundPolicy,
        cancellationEase,
        productClarity,
        mccAlignment,
      },
      flags,
      mccMedianOverall: 0, // filled in below
      auditedAt: "2026-04-23",
    };
  });

  // Compute MCC median and fill in per-audit.
  const byMcc = new Map<string, number[]>();
  merchants.forEach((m, i) => {
    const arr = byMcc.get(m.mcc) ?? [];
    arr.push(audits[i].overall);
    byMcc.set(m.mcc, arr);
  });
  const medianByMcc = new Map<string, number>();
  byMcc.forEach((scores, mcc) => {
    const sorted = [...scores].sort((a, b) => a - b);
    const mid = Math.floor(sorted.length / 2);
    const median =
      sorted.length % 2 === 0 ? (sorted[mid - 1] + sorted[mid]) / 2 : sorted[mid];
    medianByMcc.set(mcc, Math.round(median));
  });
  merchants.forEach((m, i) => {
    audits[i].mccMedianOverall = medianByMcc.get(m.mcc) ?? audits[i].overall;
  });

  return audits;
}
